import { useMemo, useState } from 'react';
import {
  Menu,
  Sidebar as ProSidebar,
  sidebarClasses,
  type MenuItemStyles,
} from 'react-pro-sidebar';
import { Link, useLocation } from '@tanstack/react-router';
import { PanelLeftClose } from '../icons';
import type { CSSObject } from '@emotion/react';
import { TruktWatermark } from '../../assets';
import { SidebarLogo } from './SidebarLogo';
import { SidebarProps } from './Sidebar.types';
import { SidebarDivider } from './SidebarDivider';
import { Typography } from '../Typography';
import { SidebarMenuItem } from './SidebarMenuItem';
import { getMenuHierarchy } from './Sidebar.helpers';
import { SidebarSubMenu } from './SidebarSubMenu';

const menuRootStyles: CSSObject = {
  padding: 0,
  '& > ul': {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0.5rem 0 0',
  },
};

const menuItemStyles: MenuItemStyles = {
  button: {
    borderRadius: '0.375rem',
    height: '2rem',
    justifyContent: 'flex-start',
    padding: '0.5rem',
    width: '100%',
  },
  icon: {
    alignItems: 'center',
    display: 'flex',
    height: '1rem',
    justifyContent: 'center',
    lineHeight: '1rem',
    marginRight: '0.5rem',
    minWidth: '1rem',
    width: '1rem',
  },
  label: {
    flexGrow: 1,
    textAlign: 'left',
  },
  SubMenuExpandIcon: {
    flexShrink: 0,
    marginLeft: 'auto',
  },
  subMenuContent: {
    backgroundColor: 'transparent',
    padding: '0 1rem',
    '& > ul': {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      padding: '0.5rem 0 0',
    },
  },
};

const footerMenuRootStyles: CSSObject = {
  padding: 0,
  '& > ul': {
    padding: 0,
  },
};

export const Sidebar = ({
  items = [],
  accountInfo,
  version,
  onLogoutClick,
  logo,
  itemsSuffix,
}: SidebarProps) => {
  const menuHierarchy = useMemo(() => getMenuHierarchy(items), [items]);
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string>(location.pathname);
  const accountEmail = accountInfo?.email;

  const onOpenChange = (menuId: string) => {
    if (menuId !== openMenu) {
      const menuIdx = menuHierarchy[openMenu]?.findIndex((id) => id === menuId) ?? -1;
      if (menuIdx < 0) return setOpenMenu(menuId);

      const newOpenMenu = menuHierarchy[openMenu][menuIdx - 1];
      return setOpenMenu(newOpenMenu ?? '');
    }
    const hierarchy = menuHierarchy[menuId];
    return setOpenMenu(hierarchy[hierarchy.length - 2]);
  };

  return (
    <ProSidebar
      rootStyles={{
        boxSizing: 'border-box',
        height: '100dvh',
        maxHeight: '100dvh',
        overflow: 'hidden',
        [`.${sidebarClasses.container}`]: {
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        },
      }}
      className={[
        'relative !h-screen !w-52 !min-w-52 !border-r-0',
        '[&_.ps-sidebar-container]:!bg-bg-primary',
        '[&_.ps-submenu-expand-icon]:text-bg-secondary',
        '[&_.ps-menu-button]:transition [&_.ps-menu-button]:ease-linear',
        '[&_.ps-menu-button:hover]:!bg-bg-secondary [&_.ps-menu-button:hover_*]:text-bg-primary',
        '[&_.ps-menuitem-root_*]:text-secondary-100',
        '[&_.ps-menuitem-root.ps-active_.ps-menu-button]:!bg-bg-secondary',
        '[&_.ps-menuitem-root.ps-active_*]:text-bg-primary',
        '[&_.ps-submenu-content]:!list-none [&_.ps-submenu-content]:transition',
      ].join(' ')}
    >
      <TruktWatermark className="absolute bottom-[0.1rem] -left-[10.5rem] -z-[1]" width={450} />
      <div className="flex shrink-0 flex-col gap-1 px-2 pt-2">
        <Link className="mb-2" to="/">
          <SidebarLogo logo={logo} />
        </Link>

        {accountInfo?.name && (
          <Typography className="text-center !text-secondary-400">{accountInfo.name}</Typography>
        )}

        {accountEmail && (
          <Typography className="text-center !text-secondary-400">{accountEmail}</Typography>
        )}
      </div>
      <SidebarDivider />
      <div
        id="sidebar-scroll-container"
        className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2"
      >
        <Menu rootStyles={menuRootStyles} menuItemStyles={menuItemStyles}>
          {items.map((item) => (
            <SidebarSubMenu
              key={item.id}
              item={item}
              menuHierarchy={menuHierarchy}
              onOpenChange={onOpenChange}
              openMenu={openMenu}
            />
          ))}
        </Menu>
      </div>
      <div id="sidebar-footer" className="shrink-0 pb-4">
        {itemsSuffix && (
          <>
            {itemsSuffix}
            <SidebarDivider />
          </>
        )}
        <Typography className="text-center !text-secondary-400">Version: {version}</Typography>
        <SidebarDivider />
        <Menu
          className="px-2 py-1"
          rootStyles={footerMenuRootStyles}
          menuItemStyles={menuItemStyles}
        >
          <SidebarMenuItem
            icon={<PanelLeftClose className="size-4 text-bg-secondary" />}
            label="Logout"
            onClick={onLogoutClick}
          />
        </Menu>
      </div>
    </ProSidebar>
  );
};
